import { useEffect, useState } from 'react';
import { chequeBookApi } from '@/api/chequeBook.api';
import type { ChequeAllocation } from '@/types/chequeBook.types';

interface Props {
  /** Null until the line names an account — until then there is nothing to describe. */
  bankAccountId: string | null;
}

/**
 * Which numbers a line's account can draw a cheque from (ML-03).
 *
 * Lives in the line's detail rather than in the grid cell. This is guidance, not data: it repeats
 * verbatim for every line drawn on the same account, and stacked under the number it made a cheque
 * row five text lines tall — stretching Amount, CCY and Remarks into tall empty boxes beside it.
 * The cell keeps the one thing that must be seen without asking, which is a number belonging to no
 * book at all.
 */
export const ChequeBookGuidance = ({ bankAccountId }: Props) => {
  const [allocation, setAllocation] = useState<ChequeAllocation | null>(null);

  useEffect(() => {
    if (!bankAccountId) { setAllocation(null); return; }

    let current = true;
    chequeBookApi.getAllocation(bankAccountId)
      .then(res => { if (current) setAllocation(res.data?.data ?? null); })
      .catch(() => { if (current) setAllocation(null); });

    return () => { current = false; };
  }, [bankAccountId]);

  const books = allocation?.books ?? [];

  if (!allocation?.isManaged || books.length === 0)
    return (
      <span className="text-slate-400 dark:text-slate-500" data-testid="cheque-book-hint">
        No cheque book registered for this account — any number is accepted.
      </span>
    );

  return (
    <span className="text-slate-600 dark:text-slate-300" data-testid="cheque-book-hint">
      {books.map((b, i) => (
        <span key={b.chequeBookId}>
          {i > 0 && <span className="text-slate-300 dark:text-slate-600"> · </span>}
          {b.bookCode}{' '}
          <span className="font-mono">{b.firstChequeNumber}–{b.lastChequeNumber}</span>
          {b.nextChequeNumber
            ? <> (next <span className="font-mono">{b.nextChequeNumber}</span>)</>
            : ' (finished)'}
        </span>
      ))}
    </span>
  );
};
